import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { queryExecutionRuns } from '@/features/executionRuns/data/executionRuns';
import { useExecutionRunsPageViewModel } from '@/features/executionRuns/hooks/useExecutionRunsPageViewModel';
import { ExecutionRunsPageCharts } from '@/features/executionRuns/pages/ExecutionRunsPageCharts';
import { ExecutionRunsPageDialogs } from '@/features/executionRuns/pages/ExecutionRunsPageDialogs';
import {
  resolveExecutionRunsRouteNotice,
  resolveForceOpenTutorial,
  resolveOnboardingJustCompleted,
  type ExecutionRunsPageLocationState,
} from '@/features/executionRuns/pages/executionRunsPageLocationState';
import { buildExecutionRunsPageTableQuery } from '@/features/executionRuns/pages/executionRunsPageTableQuery';
import { RouteNoticeBanner } from '@/features/executionRuns/pages/ExecutionRunsRouteNoticeBanner';
import {
  executionRunsTableDefinition,
  type ExecutionRunsSortKey,
} from '@/features/executionRuns/pages/executionRunsTableDefinition';
import { ExecutionRunsTableV2Panel } from '@/features/executionRuns/pages/ExecutionRunsTableV2Panel';
import {
  resolveExecutionRunsToolbarDates,
  resolveExecutionRunsToolbarQuickFilter,
} from '@/features/executionRuns/pages/executionRunsToolbar';
import { ExecutionRunsUploadAction } from '@/features/executionRuns/pages/ExecutionRunsUploadAction';
import { buildExecutionRunsUploadProjectOptions } from '@/features/executionRuns/pages/executionRunsUploadProjectOptions';
import { usePendingPostOnboardingState } from '@/features/executionRuns/pages/usePendingPostOnboardingState';
import { PageLoadingFallback } from '@/features/metrics/components/PageLoadingFallback';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { usePostOnboardingTutorialState } from '@/features/postOnboardingTutorial';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { TestingListEmptyState } from '@/features/shared/components/TestingListEmptyState';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getErrorCopy } from '@/shared/copy/errors';
import { gridPaths, queryKeys, useOrganisationEntitlements } from '@/shared/hooks';
import { resolvePlanStatus } from '@/shared/plans/planStatus';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives';
import { PlayCircleFilledWhiteOutlinedIcon } from '@/ui/primitives/icons';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { ExecutionRunDto } from '@flowqa/shared';
import type { ReactElement } from 'react';
const ExecutionRunsPageContent = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const showForcedLoadingState = searchParams.get('loadState') === 'loading';
  const locationState = location.state as ExecutionRunsPageLocationState;
  const routeNotice = resolveExecutionRunsRouteNotice(locationState);
  const forceOpenTutorial = resolveForceOpenTutorial(searchParams.get('tutorial'));
  const [visibleRouteNotice, setVisibleRouteNotice] = useState<string | null>(routeNotice);
  const { groupErroredWithFailed, statusPresentationLoading } = useProjectStatusPresentation();
  const pendingPostOnboardingState = usePendingPostOnboardingState(session?.userId);
  const onboardingJustCompleted = resolveOnboardingJustCompleted(
    locationState,
    pendingPostOnboardingState,
  );
  const {
    range,
    setRange,
    searchValue,
    setSearchValue,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    projectFilterOptions,
    showProjectFilter,
    statusOptions,
    onStatusesChange,
    onTagsChange,
    onStatusToggle,
    onProjectsChange,
    executionRunMetricCards,
    executionRunMetricsLoading,
    visibleMetricKeys,
    selectedProjectId,
    tagOptions,
    chart,
    datePicker,
    filters,
    uploadDialog,
  } = useExecutionRunsPageViewModel();
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const entitlements = entitlementsQuery.data ?? null;
  const [executionRunsSortKey] = useState<ExecutionRunsSortKey>(
    executionRunsTableDefinition.defaultSortKey,
  );
  const [executionRunsSortDirection] = useState<'asc' | 'desc'>(
    executionRunsTableDefinition.defaultSortDirection,
  );
  const [, setExecutionRunsMeta] = useState<TableQueryMeta<ExecutionRunDto> | null>(null);
  const executionRunsAvailableTags = useMemo(
    () =>
      Array.from(new Map(tagOptions.map((option) => [option.value, option])).values()).sort(
        (left, right) => left.label.localeCompare(right.label),
      ),
    [tagOptions],
  );
  const executionRunsQueryKey = useMemo(
    () => [
      ...queryKeys.dataGrid(gridPaths.executionRuns),
      'toolbar',
      executionRunsSortKey,
      executionRunsSortDirection,
    ],
    [executionRunsSortDirection, executionRunsSortKey],
  );
  const projectExecutionRunsTotalQueryKey = useMemo(
    () =>
      queryKeys.dataGrid(
        gridPaths.executionRuns,
        'project-total',
        selectedOrganisationId ?? null,
        selectedProjectId,
      ),
    [selectedOrganisationId, selectedProjectId],
  );
  const projectExecutionRunsTotalQuery = useQuery({
    queryKey: projectExecutionRunsTotalQueryKey,
    enabled: selectedProjectId !== null,
    staleTime: 30_000,
    queryFn: async (): Promise<number> => {
      if (selectedProjectId === null) return 0;
      const result = await queryExecutionRuns(
        {
          page: 1,
          pageSize: 1,
          filters: buildEqFilter('projectId', selectedProjectId),
        },
        selectedOrganisationId,
      );
      return result.total;
    },
  });
  const executionRunsQueryFn = useMemo(
    () =>
      buildExecutionRunsPageTableQuery({
        query: queryExecutionRuns,
        extras: {
          includeSummary: true,
          includeSummaryDetails: false,
        },
        sortFieldMap: executionRunsTableDefinition.sortFieldMap,
        sortKey: executionRunsSortKey,
        sortDirection: executionRunsSortDirection,
      }),
    [executionRunsSortDirection, executionRunsSortKey],
  );
  const planStatus = useMemo(
    () =>
      resolvePlanStatus({
        entitlements,
        projects: uploadDialog.projectOptions.map((project) => ({
          id: project.id,
          name: project.name,
          slug: project.slug,
          badgeChars: project.badgeChars,
          badgeColor: project.badgeColor,
          badgeEnabled: project.badgeEnabled,
          createdAt: project.createdAt,
        })),
        selectedProjectId,
      }),
    [entitlements, selectedProjectId, uploadDialog.projectOptions],
  );
  const tutorial = usePostOnboardingTutorialState({
    userId: session?.userId ?? null,
    organisationId: selectedOrganisationId ?? null,
    projectId: selectedProjectId ?? null,
    onboardingCompleted: session?.onboardingCompleted === true,
    onboardingJustCompleted,
    forceOpen: forceOpenTutorial,
  });
  const uploadBlockedReason = planStatus.uploadBlockedReason;
  const uploadProjectOptions = useMemo(
    () =>
      buildExecutionRunsUploadProjectOptions({
        planProjectOptions: planStatus.projectOptions,
        projectOptions: uploadDialog.projectOptions,
      }),
    [planStatus.projectOptions, uploadDialog.projectOptions],
  );
  const handleUploadConfirm = (): void => {
    const projectId = selectedProjectId ?? uploadDialog.uploadProjectId;
    if (!projectId) {
      uploadDialog.setUploadError(getErrorCopy('upload.selectProject'));
      return;
    }
    const selectedProject = uploadProjectOptions.find((project) => project.id === projectId);
    if (selectedProject?.locked) {
      uploadDialog.setUploadError(
        selectedProject.lockReason ?? 'Uploads are locked for this project on the current plan.',
      );
      return;
    }
    if (uploadDialog.selectedUploadFiles.length === 0) {
      uploadDialog.setUploadError(getErrorCopy('upload.selectSupportedFile'));
      return;
    }
    uploadDialog.setUploadError(null);
    const filesToUpload = uploadDialog.selectedUploadFiles;
    const tagsToUpload = uploadDialog.uploadTagValues;
    const tagColorsToUpload = uploadDialog.uploadTagColors;
    uploadDialog.closeUploadDialog();
    uploadDialog.startUpload(projectId, filesToUpload, tagsToUpload, tagColorsToUpload);
  };
  useEffect(() => {
    setVisibleRouteNotice(routeNotice);
    if (!routeNotice) return;
    const { notice: _notice, ...remainingState } = locationState ?? {};
    const nextState = Object.keys(remainingState).length > 0 ? remainingState : null;
    void navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: nextState,
    });
  }, [location.pathname, location.search, locationState, navigate, routeNotice]);
  const executionRunsToolbarDates = useMemo(
    () =>
      resolveExecutionRunsToolbarDates({
        hasCustomDates: datePicker.hasCustomDates,
        startDay: datePicker.startDay,
        endDay: datePicker.endDay,
      }),
    [datePicker.endDay, datePicker.hasCustomDates, datePicker.startDay],
  );
  const executionRunsNoRowsText = useMemo(
    () =>
      formatRangeWindowNoDataText(
        getEmptyStateMessage('entity.noneFound', { entity: 'executionRun' }),
        range,
        datePicker.hasCustomDates,
      ),
    [datePicker.hasCustomDates, range],
  );
  const executionRunsUnifiedToolbarState = useMemo(
    () => ({
      search: searchValue,
      setSearch: setSearchValue,
      selectedStatuses,
      setSelectedStatuses: onStatusesChange,
      selectedTags,
      setSelectedTags: onTagsChange,
      selectedProjects,
      setSelectedProjects: onProjectsChange,
      projectOptions: showProjectFilter ? projectFilterOptions : undefined,
      noRowsText: executionRunsNoRowsText,
      dateQuickFilter: resolveExecutionRunsToolbarQuickFilter(range),
      onDateQuickFilterChange: setRange,
      fromDate: executionRunsToolbarDates.fromDate,
      toDate: executionRunsToolbarDates.toDate,
      onFromDateChange: (next: string | null) => {
        datePicker.onStartDayChange(next);
      },
      onToDateChange: (next: string | null) => {
        datePicker.onEndDayChange(next);
      },
    }),
    [
      datePicker,
      executionRunsNoRowsText,
      executionRunsToolbarDates.fromDate,
      executionRunsToolbarDates.toDate,
      onProjectsChange,
      onStatusesChange,
      onTagsChange,
      projectFilterOptions,
      range,
      searchValue,
      selectedProjects,
      selectedStatuses,
      selectedTags,
      setRange,
      setSearchValue,
      showProjectFilter,
    ],
  );
  const showUploadFirstState =
    !uploadDialog.hasUploadRefresh &&
    selectedProjectId !== null &&
    projectExecutionRunsTotalQuery.data === 0;
  const projectExecutionRunsTotalLoading =
    selectedProjectId !== null && projectExecutionRunsTotalQuery.data === undefined;
  const showDataSurfaces =
    selectedProjectId === null || projectExecutionRunsTotalLoading || !showUploadFirstState;
  const showProjectLoadingState = projectExecutionRunsTotalLoading && !showForcedLoadingState;
  return (
    <ListPageTemplate
      headerActionsMode="separate"
      hideHeader={showUploadFirstState || showProjectLoadingState}
      title={
        <Text role="pageTitle" component="h1">
          Execution Runs
        </Text>
      }
      titleActions={
        showDataSurfaces && !showProjectLoadingState ? (
          <ExecutionRunsUploadAction
            disabledReason={uploadBlockedReason}
            onClick={uploadDialog.openUploadDialog}
          />
        ) : null
      }
      charts={
        showDataSurfaces && !showProjectLoadingState ? (
          <ExecutionRunsPageCharts
            availableTags={executionRunsAvailableTags}
            groupErroredWithFailed={groupErroredWithFailed}
            loading={
              showForcedLoadingState ||
              authLoading ||
              statusPresentationLoading ||
              executionRunMetricsLoading ||
              projectExecutionRunsTotalLoading
            }
            metricCards={executionRunMetricCards}
            onStatusToggle={onStatusToggle}
            projectId={selectedProjectId}
            selectedStatusIds={selectedStatuses}
            showPoints={chart.showPoints}
            statusFilterLabel={executionRunsTableDefinition.statusFilterLabel}
            statusOptions={statusOptions}
            suppressAnimation={chart.suppressAnimation}
            toolbarState={executionRunsUnifiedToolbarState}
            visibleMetricKeys={visibleMetricKeys}
          />
        ) : null
      }
      content={
        <Stack gap="xl">
          <RouteNoticeBanner notice={visibleRouteNotice} setNotice={setVisibleRouteNotice} />
          {showProjectLoadingState ? (
            <PageLoadingFallback loadingMessageKey="executionRuns" />
          ) : showUploadFirstState ? (
            <TestingListEmptyState
              body="Get started by uploading your first results file to see execution run insights, trends and quality metrics."
              icon={PlayCircleFilledWhiteOutlinedIcon}
              title={getEmptyStateMessage('entity.noneYetForProject', { entity: 'executionRun' })}
              uploadDisabled={uploadBlockedReason !== null}
              uploadDisabledReason={uploadBlockedReason}
              onUploadClick={uploadDialog.openUploadDialog}
            />
          ) : showDataSurfaces ? (
            <ExecutionRunsTableV2Panel
              active
              fixedFilters={filters.executionRuns}
              forceLoading={showForcedLoadingState || projectExecutionRunsTotalLoading}
              projectOptions={uploadDialog.projectOptions}
              queryFn={executionRunsQueryFn}
              queryKey={executionRunsQueryKey}
              search={searchValue}
              tagOptions={tagOptions}
              onQueryMeta={setExecutionRunsMeta}
            />
          ) : null}
        </Stack>
      }
      footer={
        <ExecutionRunsPageDialogs
          handleUploadConfirm={handleUploadConfirm}
          navigate={navigate}
          selectedOrganisationId={selectedOrganisationId}
          selectedProjectId={selectedProjectId}
          tutorial={tutorial}
          uploadDialog={uploadDialog}
          uploadBlockedReason={uploadBlockedReason}
          uploadProjectOptions={uploadProjectOptions}
        />
      }
    />
  );
};
export { ExecutionRunsPageContent };
